import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { Card, Text } from "@src/appV2/redesign/system/ui-components";

import { CancellationType, type CancelledShiftDetailsData } from "../../api/cancelledShiftTypes";
import { ShiftInfoRow } from "../../ShiftDetails/components/ShiftInfoRow";
import { CancellationInfoCard } from "./CancellationInfoCard";
import { ClipboardScoreImpactCard } from "./ClipboardScoreImpactCard";
import { PayEligibilityCard } from "./PayEligibilityCard";
import { StatusPill } from "./StatusPill";

interface CancelledShiftDetailsContentProps {
  readonly data: CancelledShiftDetailsData;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
}

export function CancelledShiftDetailsContent({
  data,
  payoutDestination,
}: CancelledShiftDetailsContentProps) {
  const formattedDate = formatShortDateWithWeekday(data.startAt, { timeZone: data.timezone });
  const scheduledTimeRange = `${formatTime(data.startAt, { timeZone: data.timezone })} - ${formatTime(
    data.endAt,
    { timeZone: data.timezone }
  )}`;
  const cancellationTypeLabel =
    data.cancellationType === CancellationType.SENT_HOME ? "Sent Home" : "Canceled by Workplace";

  return (
    <Stack spacing={4}>
      <HeroCard
        cancellationTypeLabel={cancellationTypeLabel}
        formattedDate={formattedDate}
        qualificationName={data.qualificationName}
        workplaceName={data.workplaceName}
      />

      <ScheduledShiftCard scheduledTimeRange={scheduledTimeRange} />

      <CancellationInfoCard data={data} />

      <PayEligibilityCard data={data} payoutDestination={payoutDestination} />

      <ClipboardScoreImpactCard isSentHome={data.cancellationType === CancellationType.SENT_HOME} />
    </Stack>
  );
}

interface HeroCardProps {
  readonly workplaceName: string;
  readonly formattedDate: string;
  readonly qualificationName: string | undefined;
  readonly cancellationTypeLabel: string;
}

function HeroCard(props: HeroCardProps) {
  const { workplaceName, formattedDate, qualificationName, cancellationTypeLabel } = props;
  const subtitle = isDefined(qualificationName)
    ? `${formattedDate} · ${qualificationName}`
    : formattedDate;

  return (
    <Card variant="quaternary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={1} alignItems="center">
        <StatusPill label={cancellationTypeLabel} variant="neutral" />
        <Text semibold variant="h5" sx={{ textAlign: "center" }}>
          {workplaceName}
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({ color: theme.palette.text.secondary, textAlign: "center" })}
        >
          {subtitle}
        </Text>
      </Stack>
    </Card>
  );
}

interface ScheduledShiftCardProps {
  readonly scheduledTimeRange: string;
}

function ScheduledShiftCard({ scheduledTimeRange }: ScheduledShiftCardProps) {
  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Scheduled Shift
        </Text>
        <ShiftInfoRow label="Time" value={scheduledTimeRange} />
      </Stack>
    </Card>
  );
}
